
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/feature/AuthProvider';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(username, password);
      
      if (result.success) {
        // Successful login - navigate to dashboard
        navigate('/', { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const demoUsers = [
    { username: 'admin', password: 'admin123', role: 'Administrator', desc: 'Full system access' },
    { username: 'dr.smith', password: 'doctor123', role: 'Doctor', desc: 'Patient care & prescriptions' },
    { username: 'staff.mary', password: 'staff123', role: 'Staff', desc: 'Patient management' },
    { username: 'reception', password: 'reception123', role: 'Receptionist', desc: 'Front desk operations' }
  ];

  const quickLogin = (demoUsername: string, demoPassword: string) => {
    setUsername(demoUsername);
    setPassword(demoPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Branding */}
          <div className="text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl flex items-center justify-center shadow-xl">
                <i className="ri-health-book-line text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">🌿 HomeoClinic</h1>
                <p className="text-gray-600">Homeopathic Practice Management</p>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Welcome to Your Digital Clinic
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Comprehensive homeopathic practice management with smart prescriptions, 
              patient records, and medicine database with 1400+ remedies.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <i className="ri-user-heart-line text-blue-600 text-2xl mb-2"></i>
                <h3 className="font-semibold text-gray-900">Patient Management</h3>
                <p className="text-sm text-gray-600">Complete patient records</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <i className="ri-file-list-3-line text-green-600 text-2xl mb-2"></i>
                <h3 className="font-semibold text-gray-900">Smart Prescriptions</h3>
                <p className="text-sm text-gray-600">Auto-suggestions & printing</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <i className="ri-medicine-bottle-line text-purple-600 text-2xl mb-2"></i>
                <h3 className="font-semibold text-gray-900">Medicine Database</h3>
                <p className="text-sm text-gray-600">1400+ homeopathic remedies</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <i className="ri-shield-user-line text-red-600 text-2xl mb-2"></i>
                <h3 className="font-semibold text-gray-900">Role-based Access</h3>
                <p className="text-sm text-gray-600">Secure user management</p>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Sign In</h3>
                <p className="text-gray-600">Enter your credentials to access your clinic</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <i className="ri-error-warning-line text-red-600"></i>
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    label="Username or Email"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username or email"
                    required
                    icon="ri-user-line"
                  />
                </div>

                <div>
                  <div className="relative">
                    <Input
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                      icon="ri-lock-line"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <i className="ri-login-box-line"></i>
                      Sign In
                    </>
                  )}
                </Button>
              </form>

              {/* Demo Credentials */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowDemoCredentials(!showDemoCredentials)}
                  className="w-full flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-800 cursor-pointer font-medium"
                >
                  <i className="ri-information-line"></i>
                  {showDemoCredentials ? 'Hide' : 'Show'} Demo Credentials
                  <i className={`ri-arrow-${showDemoCredentials ? 'up' : 'down'}-s-line`}></i>
                </button>
                
                {showDemoCredentials && (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs text-gray-600 text-center mb-4">
                      Click any credential to auto-fill the form
                    </p>
                    {demoUsers.map((demo, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => quickLogin(demo.username, demo.password)}
                        className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-left cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{demo.role}</p>
                            <p className="text-xs text-gray-600">{demo.desc}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-mono text-gray-500">{demo.username}</p>
                            <p className="text-xs font-mono text-gray-400">{demo.password}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                Need help? Contact your system administrator
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
